// Message translation, on this device (like WhatsApp's): the browser's
// built-in Translator and LanguageDetector (Chrome / Edge 138+), so messages
// never leave the device — chats stay end-to-end encrypted. Each language
// pair's model is downloaded once by the browser.

interface Availability {
  availability(o: Record<string, string>): Promise<"unavailable" | "downloadable" | "downloading" | "available">;
}
interface Monitor {
  addEventListener(t: "downloadprogress", f: (e: { loaded: number }) => void): void;
}
interface TranslatorAPI extends Availability {
  create(o: { sourceLanguage: string; targetLanguage: string; monitor?: (m: Monitor) => void }): Promise<{ translate(text: string): Promise<string> }>;
}
interface DetectorAPI extends Availability {
  create(o?: { monitor?: (m: Monitor) => void }): Promise<{ detect(text: string): Promise<{ detectedLanguage: string; confidence: number }[]> }>;
}
const g = globalThis as unknown as { Translator?: TranslatorAPI; LanguageDetector?: DetectorAPI };

export const canTranslate = () => !!g.Translator && !!g.LanguageDetector;

/** The language to translate into: the browser's. */
export const myLanguage = () => (navigator.language || "en").split("-")[0].toLowerCase();

const names = (() => {
  try {
    return new Intl.DisplayNames([navigator.language || "en"], { type: "language" });
  } catch {
    return null;
  }
})();
export const languageName = (code: string) => names?.of(code) ?? code;

let detector: ReturnType<DetectorAPI["create"]> | null = null;
const translators = new Map<string, ReturnType<TranslatorAPI["create"]>>();

export class TranslateError extends Error {}

export async function translate(text: string, onProgress?: (pct: number) => void): Promise<{ text: string; from: string; to: string }> {
  if (!canTranslate()) throw new TranslateError("Translation happens on your device and needs a recent Chrome or Edge.");
  const to = myLanguage();
  const monitor = (m: Monitor) => m.addEventListener("downloadprogress", (e) => onProgress?.(Math.round(e.loaded * 100)));
  if (!detector) {
    detector = g.LanguageDetector!.create({ monitor });
    detector.catch(() => (detector = null));
  }
  const best = (await (await detector).detect(text))[0];
  if (!best || best.detectedLanguage === "und" || best.confidence < 0.4) throw new TranslateError("Couldn't tell what language this is.");
  const from = best.detectedLanguage.split("-")[0];
  if (from === to) throw new TranslateError(`This message is already in ${languageName(to)}.`);
  const pair = { sourceLanguage: from, targetLanguage: to };
  if ((await g.Translator!.availability(pair)) === "unavailable") throw new TranslateError(`Can't translate ${languageName(from)} to ${languageName(to)} on this device.`);
  const key = from + ">" + to;
  let t = translators.get(key);
  if (!t) {
    t = g.Translator!.create({ ...pair, monitor });
    t.catch(() => translators.delete(key));
    translators.set(key, t);
  }
  return { text: await (await t).translate(text), from, to };
}
